/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { FunctionComponent } from "react";
import { MenuItem, ToggleSwitch, Header, HeaderBreadcrumbs, HeaderButton, Tooltip } from "@itwin/itwinui-react";
import { DoublyLinkedList } from "./linked-list/DoublyLinkedList";
import { DoublyLinkedNode } from "./linked-list/DoublyLinkedNode";
import { Annotation } from "../../types/Annotation";
import styles from "./WalkthroughHeader.module.css";

export interface AnnotationsHeaderProps {
  step?: DoublyLinkedNode<Annotation>;
  steps: DoublyLinkedList<Annotation>;
  onStepChange: (nextStep: DoublyLinkedNode<Annotation>) => void;
  codeHighlight: boolean;
  onCodeHighlight: (state: boolean) => void;
}

export const WalkthroughHeader: FunctionComponent<AnnotationsHeaderProps> = ({ step, steps, codeHighlight, onStepChange, onCodeHighlight }) => {
  //const [codeHighlight, setCodeHighlight] = useState(true);

  const codeHighLightChange = () => {
    const value = !codeHighlight;
    //setCodeHighlight(value);
    onCodeHighlight(value);
  }

  const onMenuItemClick = (close: () => void) => (value: unknown) => {
    const step = steps.find((item) => item.value.index === (value as Annotation).index);
    if (!step)
      return;

    onStepChange(step);
    close();
  }

  const getTitle = (index?: string, title?: string) => {
    return index === "0" ? title : `${index}. ${title}`;
  }

  const walkthroughMenuBuilder = (close: () => void) => {
    return steps.map((step) => {
      // Add indentation to submenu items, i.e. those containing "." character in the number
      const matches = step.value.index.match(/\./gi) || [];
      const tabs = matches.map(() => <span style={{ display: "inline-block", width: "12px" }} />);

      return (
        <MenuItem key={step.value.index} value={step.value} onClick={onMenuItemClick(close)}>
          {tabs}{getTitle(step.value.index, step.value.title)}
        </MenuItem>);
    });
  }

  return (
    <Header
      appLogo={null}
      className={styles.header}
      isSlim={true}
      breadcrumbs={<HeaderBreadcrumbs items={[<HeaderButton
        key="walkthrough-menu"
        menuItems={walkthroughMenuBuilder}
        name={getTitle(step?.value.index, step?.value.title)}
      />]} />}
      actions={[
        <Tooltip placement="top" delay={500} content="Switch on or off code highlight">
          <ToggleSwitch label="Spotlight" labelPosition="right" checked={codeHighlight} onChange={codeHighLightChange} style={{ marginRight: "8px" }} />
        </Tooltip>
      ]}
    />
  );
}
